import { useEffect, useState, useCallback, useMemo, type ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  TextField,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Business,
  People,
  Lock,
  Clear,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import { useTranslation } from 'react-i18next';
import { RequirePermission } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useListSearchParams } from '../hooks/useListSearchParams';
import { useLanguage } from '../hooks/useLanguage';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { formatDateOnly, formatDateTime } from '../utils/date';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import type { SubscriptionPlanType, Tenant, TenantActiveSubscriptionCycle } from '../types';
import ListActionButton from '../components/ListActionButton';
import usePageTitle from '../hooks/usePageTitle';

type TenantDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';

interface TenantFilterOption {
  value: string;
  label: string;
}

interface TenantDateRangeOption extends TenantFilterOption {
  value: TenantDateRangePreset;
}

const FILTER_MULTI_VALUE_SEPARATOR = ',';
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARACTERS = 3;
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';
const TENANT_DATE_RANGE_PRESETS: readonly TenantDateRangePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'period',
];

function isTenantDateRangePreset(value: string | null | undefined): value is TenantDateRangePreset {
  if (!value) {
    return false;
  }
  return TENANT_DATE_RANGE_PRESETS.includes(value as TenantDateRangePreset);
}

export default function TenantsPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:tenants.title'));
  const { locale } = useNumberFormat();
  const { language } = useLanguage();
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalTenants, setTotalTenants] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);
  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  // Dialog hooks
  const { confirm, alert: showAlert, DialogComponent: ConfirmDialogComponent } = useConfirmDialog();

  const parseMultiFilterValue = useCallback((rawValue: string | null | undefined): string[] => {
    if (!rawValue) {
      return [];
    }
    return Array.from(
      new Set(
        rawValue
          .split(FILTER_MULTI_VALUE_SEPARATOR)
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    );
  }, []);

  const serializeMultiFilterValue = useCallback((values: string[]): string | undefined => {
    const normalizedValues = values
      .map((value) => value.trim())
      .filter(Boolean);
    if (normalizedValues.length === 0) {
      return undefined;
    }
    return normalizedValues.join(FILTER_MULTI_VALUE_SEPARATOR);
  }, []);

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'search', 'subscription_type', 'date_range', 'start_date', 'end_date'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const statusFilterValue = filters.status ?? '';
  const searchFilterValue = filters.search ?? '';
  const subscriptionTypeFilterValues = useMemo(
    () => parseMultiFilterValue(filters.subscription_type),
    [filters.subscription_type, parseMultiFilterValue],
  );
  const startDateFilterValue = filters.start_date ?? '';
  const endDateFilterValue = filters.end_date ?? '';
  const orderingParam = ordering || '-created_at';
  const selectedDateRange = useMemo<TenantDateRangePreset | null>(() => {
    if (isTenantDateRangePreset(filters.date_range)) {
      return filters.date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.date_range, startDateFilterValue]);
  const startDateValue = useMemo<Dayjs | null>(() => {
    if (!startDateFilterValue) {
      return null;
    }
    const value = dayjs(startDateFilterValue);
    return value.isValid() ? value : null;
  }, [startDateFilterValue]);
  const endDateValue = useMemo<Dayjs | null>(() => {
    if (!endDateFilterValue) {
      return null;
    }
    const value = dayjs(endDateFilterValue);
    return value.isValid() ? value : null;
  }, [endDateFilterValue]);
  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || null,
        endDate: endDateFilterValue || null,
      };
    }
    if (!selectedDateRange) {
      return {
        startDate: null,
        endDate: null,
      };
    }

    const today = dayjs();
    if (selectedDateRange === 'today') {
      const value = today.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    if (selectedDateRange === 'yesterday') {
      const value = today.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    return {
      startDate: today.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT),
      endDate: today.format(DATE_FILTER_PARAM_FORMAT),
    };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('pages:tenants.status.active') },
      { value: 'inactive', label: t('pages:tenants.status.inactive') },
    ],
    [t],
  );
  const subscriptionTypeOptions = useMemo<TenantFilterOption[]>(
    () => [
      { value: 'ai', label: t('pages:tenants.billing.subscriptions.planType.ai') },
      { value: 'reverse_ivr', label: t('pages:tenants.billing.subscriptions.planType.reverseIvr') },
    ],
    [t],
  );
  const selectedSubscriptionTypeOptions = useMemo(
    () => subscriptionTypeOptions.filter((option) => subscriptionTypeFilterValues.includes(option.value)),
    [subscriptionTypeFilterValues, subscriptionTypeOptions],
  );
  const dateRangeOptions = useMemo<TenantDateRangeOption[]>(
    () => [
      { value: 'today', label: t('pages:tenants.filters.today') },
      { value: 'yesterday', label: t('pages:tenants.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:tenants.filters.last7Days') },
      { value: 'period', label: t('pages:tenants.filters.period') },
    ],
    [t],
  );
  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );
  const getSubscriptionTypeLabel = useCallback(
    (subscriptionType: SubscriptionPlanType) =>
      subscriptionType === 'reverse_ivr'
        ? t('pages:tenants.billing.subscriptions.planType.reverseIvr')
        : t('pages:tenants.billing.subscriptions.planType.ai'),
    [t],
  );
  const hasActiveFilters = Boolean(
    searchFilterValue ||
    searchInput.trim() ||
    statusFilterValue ||
    subscriptionTypeFilterValues.length > 0 ||
    selectedDateRange ||
    startDateFilterValue ||
    endDateFilterValue,
  );

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }
      const serializedSubscriptionTypeFilter = serializeMultiFilterValue(subscriptionTypeFilterValues);
      if (serializedSubscriptionTypeFilter) {
        params.subscription_type = serializedSubscriptionTypeFilter;
      }
      if (resolvedDateParams.startDate) {
        params.start_date = resolvedDateParams.startDate;
      }
      if (resolvedDateParams.endDate) {
        params.end_date = resolvedDateParams.endDate;
      }

      const response = await api.get('/api/tenants/', { params });
      const data = response.data.results ?? response.data;
      setTenants(Array.isArray(data) ? data : []);
      setTotalTenants(response.data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (error) {
      console.error('Error loading tenants:', error);
      setTenants([]);
      setTotalTenants(0);
    } finally {
      setLoading(false);
    }
  }, [
    orderingParam,
    page,
    resolvedDateParams.endDate,
    resolvedDateParams.startDate,
    rowsPerPage,
    searchFilterValue,
    serializeMultiFilterValue,
    statusFilterValue,
    subscriptionTypeFilterValues,
  ]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    setSearchInput(searchFilterValue);
  }, [searchFilterValue]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = searchInput.trim();
      if (normalizedSearch.length > 0 && normalizedSearch.length < SEARCH_MIN_CHARACTERS) {
        return;
      }

      const nextSearchParam = normalizedSearch.length === 0 ? undefined : normalizedSearch;
      const currentSearchParam = searchFilterValue || undefined;
      if (currentSearchParam === nextSearchParam) {
        return;
      }

      setParams({ search: nextSearchParam }, { resetPage: true });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchFilterValue, searchInput, setParams]);

  const handleDelete = async (tenant: Tenant) => {
    if (tenant.isSystem) {
      await showAlert({
        title: t('pages:tenants.cannotDelete.title'),
        message: t('pages:tenants.cannotDelete.message'),
        type: 'warning',
        icon: <Delete color="error" />,
        confirmText: t('pages:tenants.cannotDelete.confirm'),
      });
      return;
    }

    const confirmed = await confirm({
      title: t('pages:tenants.deleteTenant.title'),
      message: t('pages:tenants.deleteTenant.message', { name: tenant.name }),
      type: 'error',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('pages:tenants.deleteTenant.confirm'),
    });
    if (!confirmed) return;

    try {
      await api.delete(`/api/tenants/${tenant.id}/`);
      fetchTenants();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      console.error('Error deleting tenant:', error);
      await showAlert({
        title: t('pages:tenants.deleteFailed.title'),
        message: error.response?.data?.error || t('pages:tenants.deleteFailed.message'),
        type: 'error',
        confirmText: t('pages:tenants.deleteFailed.confirm'),
      });
    }
  };

  const formatDate = (dateString: string) => (
    formatDateTime(dateString, {
      locale,
      options: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
    })
  );
  const formatCurrentCycleRange = useCallback(
    (cycle: TenantActiveSubscriptionCycle) => {
      const startLabel = cycle.currentPeriodStart
        ? formatDateOnly(cycle.currentPeriodStart, { locale, fallback: '—' })
        : '—';
      const endLabel = cycle.currentPeriodEnd
        ? formatDateOnly(cycle.currentPeriodEnd, { locale, fallback: t('common:subscription.noEndDate') })
        : t('common:subscription.noEndDate');
      return `${startLabel} \u2192 ${endLabel}`;
    },
    [locale, t],
  );

  const headerActions = (
    <>
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={fetchTenants}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="tenants_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/tenants/new"
        >
          {t('pages:tenants.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <Business sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:tenants.title'),
          subtitle: t('pages:tenants.subtitle'),
          actions: headerActions,
        }}
        filterControls={
          <>
            <TextField
              size="small"
              label={t('common:actions.search')}
              placeholder={t('pages:tenants.filters.searchPlaceholder')}
              value={searchInput}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearchInput(event.target.value);
              }}
              sx={{ ...filterInputSx, minWidth: 240, flex: '1 1 240px' }}
            />
            <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
              <InputLabel id="tenants-status-filter-label">
                {t('pages:tenants.filters.statusLabel')}
              </InputLabel>
              <Select
                labelId="tenants-status-filter-label"
                value={statusFilterValue}
                label={t('pages:tenants.filters.statusLabel')}
                onChange={(event: SelectChangeEvent<string>) => {
                  const value = event.target.value;
                  setParams({ status: value || undefined }, { resetPage: true });
                }}
              >
                <MenuItem value="">
                  <em>{t('common:common.all')}</em>
                </MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete<TenantFilterOption, true, false, false>
              multiple
              disableCloseOnSelect
              options={subscriptionTypeOptions}
              value={selectedSubscriptionTypeOptions}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label}
              onChange={(_, selectedOptions) => {
                const nextValues = selectedOptions.map((option) => option.value);
                setParams(
                  { subscription_type: serializeMultiFilterValue(nextValues) },
                  { resetPage: true },
                );
              }}
              sx={{ minWidth: 240, flex: '1 1 240px' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t('pages:tenants.filters.subscriptionTypeLabel')}
                  sx={filterInputSx}
                />
              )}
            />
            <Autocomplete<TenantDateRangeOption, false, false, false>
              options={dateRangeOptions}
              value={dateRangeOption}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label}
              onChange={(_, selectedOption) => {
                const nextDateRange = selectedOption?.value ?? null;
                if (!nextDateRange) {
                  setParams({ date_range: null, start_date: null, end_date: null }, { resetPage: true });
                  return;
                }
                if (nextDateRange === 'period') {
                  setParams({ date_range: nextDateRange }, { resetPage: true });
                  return;
                }
                setParams({ date_range: nextDateRange, start_date: null, end_date: null }, { resetPage: true });
              }}
              sx={{ minWidth: 220, flex: '1 1 220px' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t('pages:tenants.filters.currentCycleDueDateLabel')}
                  sx={filterInputSx}
                />
              )}
            />
            {selectedDateRange === 'period' ? (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                <>
                  <DatePicker
                    label={t('pages:tenants.filters.fromDate')}
                    value={startDateValue}
                    onChange={(value) => {
                      const formattedDate = value && value.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null;
                      setParams({ date_range: 'period', start_date: formattedDate }, { resetPage: true });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' },
                      },
                    }}
                  />
                  <DatePicker
                    label={t('pages:tenants.filters.toDate')}
                    value={endDateValue}
                    onChange={(value) => {
                      const formattedDate = value && value.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null;
                      setParams({ date_range: 'period', end_date: formattedDate }, { resetPage: true });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' },
                      },
                    }}
                  />
                </>
              </LocalizationProvider>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              disabled={!hasActiveFilters}
              onClick={() => {
                if (!hasActiveFilters) return;
                setParams(
                  {
                    search: null,
                    status: null,
                    subscription_type: null,
                    date_range: null,
                    start_date: null,
                    end_date: null,
                  },
                  { resetPage: true },
                );
                setSearchInput('');
              }}
              sx={{
                ...flatButtonSx,
                color: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.main, 0.6),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
              >
                {t('common:actions.clear')}
              </Button>
            </>
        }
        table={(
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '& th': {
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.75rem',
                  },
                }}
              >
                <TableCell>{columnTitle(t('common:common.name'))}</TableCell>
                <TableCell>{columnTitle(t('pages:tenants.table.email'))}</TableCell>
                <TableCell>{columnTitle(t('pages:tenants.table.currentCycle'))}</TableCell>
                <TableCell align="center">{columnTitle(t('pages:tenants.table.users'))}</TableCell>
                <TableCell>{columnTitle(t('pages:tenants.table.created'))}</TableCell>
                <TableCell align="center">{columnTitle(t('pages:tenants.table.status'))}</TableCell>
                <TableCell align="right">{columnTitle(t('pages:tenants.table.actions'))}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">{t('pages:tenants.noTenants')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((tenant) => (
                  <TableRow
                    key={tenant.id}
                    hover
                    sx={{
                      transition: 'background 0.2s',
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    <LinkTableCell to={`/tenants/${tenant.id}`}>
                    <Box>
                      <Typography fontWeight={600}>{tenant.name}</Typography>
                      {tenant.isSystem && (
                        <Chip
                          icon={<Lock />}
                          label={t('pages:tenants.status.system')}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                    </LinkTableCell>
                    <LinkTableCell to={`/tenants/${tenant.id}`}>
                      {tenant.email}
                    </LinkTableCell>
                    <TableCell>
                      {tenant.activeSubscriptionCycles && tenant.activeSubscriptionCycles.length > 0 ? (
                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                          {tenant.activeSubscriptionCycles.map((cycle) => (
                            <Box key={cycle.subscriptionType}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  fontWeight: 700,
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {getSubscriptionTypeLabel(cycle.subscriptionType)}
                              </Typography>
                              <Typography variant="body2">
                                {formatCurrentCycleRange(cycle)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('pages:tenants.table.noActiveCycle')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<People />}
                        label={tenant.userCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(tenant.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tenant.isActive ? t('pages:tenants.status.active') : t('pages:tenants.status.inactive')}
                        color={tenant.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <RequirePermission permission="tenants_update">
                        <Tooltip title={t('pages:tenants.tooltips.edit')}>
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to={`/tenants/${tenant.id}`}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </RequirePermission>
                      <RequirePermission permission="tenants_delete">
                        <Tooltip title={tenant.isSystem ? t('pages:tenants.tooltips.cannotDeleteSystem') : t('pages:tenants.tooltips.delete')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(tenant)}
                              disabled={tenant.isSystem}
                            >
                              <Delete />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </RequirePermission>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        tableFooter={
          totalTenants > 0 ? (
            <LinkPagination
              component="div"
              count={totalTenants}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common:pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                count !== -1
                  ? `${from}-${to} ${t('common:common.of')} ${count}`
                  : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
              }
              getItemAriaLabel={(type) => {
                if (type === 'first') return t('common:pagination.firstPage');
                if (type === 'last') return t('common:pagination.lastPage');
                if (type === 'next') return t('common:pagination.nextPage');
                return t('common:pagination.previousPage');
              }}
              getPageLink={getPageLink}
            />
          ) : undefined
        }
      />

      {/* Confirmation Dialog */}
      {ConfirmDialogComponent}
    </Box>
  );
}
